/**
 * Minh hoạ nhỏ cho 3 thẻ "Giảm thiểu công việc lặp lại" ở trang Bắt đầu: nhân viên AI, tự động hoá, mời đồng nghiệp.
 * Chỉ để minh hoạ (không gọi API), dựng bằng HTML/Tailwind để sắc nét và theo màu giao diện (sáng/tối).
 */
import { useTranslation } from 'react-i18next'
import { ArrowDown, Bot, CheckCheck, MessageCircle, Send, Tag, UserPlus } from 'lucide-react'
import { cn } from '@/lib/utils'

function Frame({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div aria-hidden className={cn('relative flex min-h-[9.5rem] flex-col overflow-hidden rounded-lg border px-3 py-3 text-[11px]', className)}>
      {children}
    </div>
  )
}

/** Khách hỏi → nhân viên AI trả lời ngay, kể cả ngoài giờ. */
export function AiReplyIllustration() {
  const { t } = useTranslation('growth')
  return (
    <Frame className="border-violet-200/70 bg-gradient-to-br from-violet-50 to-white dark:border-violet-900/50 dark:from-violet-950/40 dark:to-card">
      <div className="flex max-w-[78%] items-end gap-1.5">
        <span className="h-5 w-5 shrink-0 rounded-full bg-[#fde3cf]" />
        <p className="rounded-xl rounded-bl-sm bg-card px-2.5 py-1.5 shadow-sm">{t('onboardingIllustrations.aiReply.question')}</p>
      </div>
      <div className="ml-auto mt-2 flex max-w-[84%] items-end justify-end gap-1.5">
        <p className="rounded-xl rounded-br-sm bg-violet-600 px-2.5 py-1.5 text-white shadow-sm">
          {t('onboardingIllustrations.aiReply.answer')}
        </p>
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-600 text-white">
          <Bot className="h-3 w-3" />
        </span>
      </div>
      <div className="mt-auto flex flex-wrap items-center justify-between gap-1 pt-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 font-medium text-violet-700 dark:bg-violet-900/60 dark:text-violet-200">
          <span className="h-1.5 w-1.5 rounded-full bg-success" /> {t('onboardingIllustrations.aiReply.online')}
        </span>
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <CheckCheck className="h-3 w-3 text-violet-600" /> {t('onboardingIllustrations.aiReply.responseTime')}
        </span>
      </div>
    </Frame>
  )
}

/** Quy trình: có tin mới → gắn nhãn → gửi lời chào. */
export function AutomationFlowIllustration() {
  const { t } = useTranslation('growth')
  const nodes = [
    { icon: MessageCircle, label: t('onboardingIllustrations.automationFlow.newMessage'), cls: 'bg-card text-foreground' },
    { icon: Tag, label: t('onboardingIllustrations.automationFlow.tagNew'), cls: 'bg-card text-foreground' },
    { icon: Send, label: t('onboardingIllustrations.automationFlow.autoGreeting'), cls: 'bg-primary text-primary-foreground' },
  ]
  return (
    <Frame className="flex flex-col items-center justify-center gap-0.5 border-primary/15 bg-gradient-to-br from-[#eef3ff] to-white dark:from-primary/10 dark:to-card">
      {nodes.map(({ icon: Icon, label, cls }, i) => (
        <div key={label} className="flex flex-col items-center gap-0.5">
          {i > 0 && <ArrowDown className="h-3 w-3 text-primary/60" />}
          <span className={cn('inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-1 font-medium shadow-sm', cls)}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </span>
        </div>
      ))}
    </Frame>
  )
}

/** Cả đội cùng trực: hội thoại được chia theo kênh cho từng người. */
export function TeamInboxIllustration() {
  const { t } = useTranslation('growth')
  const rows = [
    { initials: 'LA', name: t('onboardingIllustrations.teamInbox.lan'), channel: 'Zalo OA', color: 'bg-[#fde3cf] text-[#c2410c]' },
    { initials: 'MI', name: t('onboardingIllustrations.teamInbox.minh'), channel: 'Facebook', color: 'bg-[#dbeafe] text-[#1d4ed8]' },
    { initials: 'HA', name: t('onboardingIllustrations.teamInbox.ha'), channel: 'Website', color: 'bg-[#dcfce7] text-[#15803d]' },
  ]
  return (
    <Frame className="border-emerald-200/70 bg-gradient-to-br from-emerald-50 to-white dark:border-emerald-900/50 dark:from-emerald-950/30 dark:to-card">
      <div className="mb-2 space-y-1.5">
        {rows.map((r) => (
          <div key={r.name} className="flex items-center gap-2 rounded-md bg-card px-2 py-1 shadow-sm">
            <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold', r.color)}>
              {r.initials}
            </span>
            <span className="font-medium">{r.name}</span>
            <span className="ml-auto truncate rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{r.channel}</span>
          </div>
        ))}
      </div>
      <span className="ml-auto mt-auto inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 font-medium text-white shadow-sm">
        <UserPlus className="h-3 w-3" /> {t('onboardingIllustrations.teamInbox.invite')}
      </span>
    </Frame>
  )
}
